/**
 * Constants for title case processing
 */
const SMALL_WORDS = /^(a|an|and|as|at|but|by|en|for|if|in|nor|of|on|or|per|the|to|vs?\.?|via)$/i;
const TOKENS = /[A-Za-z0-9\u00C0-\u00FF]+[^\s-]*/g;
const WHITESPACE = /\s/;
const IS_MANUAL_CASE = /[A-Z]|\./;

/**
 * Converts a string to title case with special handling for small words
 * @param {string} input - The string to convert to title case
 * @returns {string} The title-cased string
 * @throws {TypeError} If input is not a string
 */
export default function titleCase(input) {
  // Input validation
  if (input === null || input === undefined) {
    return input;
  }
  if (typeof input !== 'string') {
    throw new TypeError('Input must be a string');
  }
  if (!input.trim()) {
    return input;
  }

  return input.toLowerCase().replace(TOKENS, (word, index, title) => {
    // Keep original case for manually capitalized words
    if (shouldKeepOriginalCase(word)) {
      return word;
    }

    // Handle small words
    if (shouldLowercaseWord(word, index, title)) {
      return word.toLowerCase();
    }

    // Default case: capitalize first letter
    return capitalizeFirstLetter(word);
  });
}

/**
 * Determines if a word's original case should be maintained
 */
function shouldKeepOriginalCase(word) {
  return IS_MANUAL_CASE.test(word.slice(1));
}

/**
 * Determines if a word should be lowercase based on title case rules
 */
function shouldLowercaseWord(word, index, title) {
  if (index === 0) return false;
  
  const prevChar = title.charAt(index - 1);
  const nextChar = title.charAt(index + word.length);
  const isPrecededByColon = title.charAt(index - 2) === ':';
  
  return SMALL_WORDS.test(word) && 
         !isPrecededByColon &&
         (nextChar !== '-' || prevChar === '-') &&
         !WHITESPACE.test(prevChar);
}

/**
 * Capitalizes the first letter of a word and lowercases the rest
 */
function capitalizeFirstLetter(word) {
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}